import classNames from 'classnames';
import { parse, ParseConfig, unparse } from 'papaparse';
import React, { useMemo, useState } from 'react';
import { type PollSchema } from 'schema';
import { ErrorHint } from '../../../components/ErrorHint';
import { Overlay } from '../../../components/Overlay';
import { QuestionTitle } from '../../../components/QuestionTitle/QuestionTitle';
import { ZoomImageButton } from '../../../components/ZoomImageButton';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { QuestionProps } from '../../../types/poll.domain';
import { isVisibleError } from '../../../utils/isRealError';
import styles from './ClickMapPoll.module.css';
import { ImageClickMap } from './ImageClickMap';
import type { ImageClick } from './types';

interface Props extends QuestionProps {
  question: PollSchema.POLL_CLICK_MAP['data'];
}

export const ClickMapPoll = (props: Props) => {
  const { answeredOthers, onOthersChange, isAnswered, question, questionId, errorMessage, alignSurvey } = props;
  const { image, hasComment } = question;

  const [isFullscreen, setIsFullScreen] = useState(false);

  const clicks = useMemo(
    () => (answeredOthers ? parse<ImageClick>(answeredOthers, tsvOptions).data : []),
    [answeredOthers],
  );

  const handleOpenFullscreen = () => {
    setIsFullScreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullScreen(false);
  };

  const handleChange = (clicks: ImageClick[]) => {
    onOthersChange({ value: unparse(clicks, tsvOptions) });
  };

  const isPhone = useBreakpoint('phone');

  if (!image) {
    return null;
  }

  const clickMapProps = {
    image,
    value: clicks,
    onChange: handleChange,
    hasComment,
    ...(hasComment && {
      commentLabel: question.commentLabel,
      commentLimit: question.commentLimit,
    }),
  };

  const hasError = isVisibleError(errorMessage);

  return (
    <div>
      <QuestionTitle
        required={question.requiresAnswer}
        questionId={questionId}
        className={classNames(styles.title, { [c(styles.titleWithSubtitle)]: hasError })}
        alignSurvey={alignSurvey}
      >
        {question.text}
      </QuestionTitle>

      {hasError && <ErrorHint className={styles.errorHint}>{errorMessage}</ErrorHint>}

      <div className={styles.container}>
        {!isPhone && <ZoomImageButton className={styles.zoomButton} onClick={handleOpenFullscreen} />}

        <ImageClickMap {...clickMapProps} readOnly={isAnswered || (isFullscreen && !isPhone)} />
      </div>

      {!isPhone && (
        <Overlay opened={isFullscreen} onClose={handleCloseFullscreen}>
          <div className={styles.fullscreenContainer}>
            <ImageClickMap
              {...clickMapProps}
              className={styles.fullscreenMap}
              readOnly={isAnswered || !isFullscreen}
              fullscreen
            />
          </div>
        </Overlay>
      )}
    </div>
  );
};

export const tsvOptions: ParseConfig = {
  quoteChar: '"',
  escapeChar: '"',
  delimiter: '\t',
  header: true,
  newline: '\r\n',
  skipEmptyLines: true,
};
